import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { useAuthStore } from './authStore';
import { fromE164 } from '../lib/phone';
import { generateId } from '../lib/utils';
import { ApiError } from '../services/apiClient';
import { applicationApi } from '../services/applicationService';
import { INITIAL_APPLICATION_DATA, type ApplicationData } from '../types/application';

import type {
  ApplicationDataDto,
  PrefillData,
  SaveDraftRequest,
  ValidationError,
  ValidationWarning,
} from '../types/applicationApi.types';

// Summary of existing application for display
export interface DecisionContext {
  decision: 'YES' | 'NO' | 'WAITLIST';
  reasonCode?: string;
  notes?: string;
  decidedAt: string;
}

export interface ExistingApplication {
  applicationId: string;
  applicationNumber: string;
  status: string;
  currentStep?: number;
  updatedAt?: string;
  submittedAt?: string;
  firstName?: string;
  lastName?: string;
  jobPostingId?: string;
  jobPostingTitle?: string;
  jobPostingRole?: string;
  jobPostingLocation?: string;
  jobPostingSiteState?: string;
  decisionContext?: DecisionContext;
}

type StepKeys = 'stepA' | 'stepB' | 'stepC' | 'stepD' | 'stepE' | 'stepF';

function isAuthenticated(): boolean {
  return useAuthStore.getState().isAuthenticated;
}

let autoSaveInFlight = false;

// Migration function to handle old data structures
function migrateApplicationData(data: ApplicationData): ApplicationData {
  return {
    ...data,
    // Ensure stepC has certifications
    stepC: {
      ...INITIAL_APPLICATION_DATA.stepC,
      ...data.stepC,
      certifications: data.stepC?.certifications ?? INITIAL_APPLICATION_DATA.stepC.certifications,
    },
    // Ensure stepE has new fields with defaults
    stepE: {
      ...INITIAL_APPLICATION_DATA.stepE,
      ...data.stepE,
      indoorOutdoorComfort: data.stepE?.indoorOutdoorComfort ?? INITIAL_APPLICATION_DATA.stepE.indoorOutdoorComfort,
      standingPatrolTolerance:
        data.stepE?.standingPatrolTolerance ?? INITIAL_APPLICATION_DATA.stepE.standingPatrolTolerance,
      languagesSpoken: data.stepE?.languagesSpoken ?? INITIAL_APPLICATION_DATA.stepE.languagesSpoken,
      eeoDisclosureConsent: data.stepE?.eeoDisclosureConsent ?? INITIAL_APPLICATION_DATA.stepE.eeoDisclosureConsent,
    },
    // Ensure stepF has new fields
    stepF: {
      ...INITIAL_APPLICATION_DATA.stepF,
      ...data.stepF,
      consentPrivacy: data.stepF?.consentPrivacy ?? (data.stepF as { consentData?: boolean })?.consentData ?? false,
      consentMessaging:
        data.stepF?.consentMessaging ??
        (data.stepF as { consentNotifications?: boolean })?.consentNotifications ??
        false,
      consentVideoRecording: data.stepF?.consentVideoRecording ?? false,
      signatureDate: data.stepF?.signatureDate ?? '',
    },
  };
}

/**
 * Convert empty strings to undefined for API requests.
 * Note: Using ternary instead of ?? because empty strings should become undefined.
 */
function emptyToUndefined(value: string | undefined): string | undefined {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  return value ? value : undefined;
}

/**
 * Convert local ApplicationData to API SaveDraftRequest format
 */
function toApiFormat(
  data: ApplicationData,
  currentStep: number,
  jobPostingId?: string | null,
  applicationId?: string | null,
): SaveDraftRequest {
  return {
    applicationId: applicationId ?? undefined,
    // Job Posting
    jobPostingId: jobPostingId ?? undefined,

    // Step A
    firstName: emptyToUndefined(data.stepA.firstName),
    middleName: emptyToUndefined(data.stepA.middleName),
    lastName: emptyToUndefined(data.stepA.lastName),
    preferredName: emptyToUndefined(data.stepA.preferredName),
    email: emptyToUndefined(data.stepA.email),
    phone: emptyToUndefined(data.stepA.phone),
    address: emptyToUndefined(data.stepA.address),
    city: emptyToUndefined(data.stepA.city),
    state: emptyToUndefined(data.stepA.state),
    zip: emptyToUndefined(data.stepA.zip),
    language: data.stepA.language,

    // Step B
    isOver18: data.stepB.isOver18,
    isAuthorizedToWork: data.stepB.isAuthorizedToWork,

    // Step C
    licensingNotApplicable: data.stepC.licensingNotApplicable,
    florida: data.stepC.florida,
    newYork: data.stepC.newYork,
    certifications: data.stepC.certifications,

    // Step D
    roles: data.stepD.roles.length > 0 ? data.stepD.roles : undefined,
    locations: data.stepD.locations.length > 0 ? data.stepD.locations : undefined,
    shifts: data.stepD.shifts.length > 0 ? data.stepD.shifts : undefined,
    hasTransport: data.stepD.hasTransport,
    startDate: emptyToUndefined(data.stepD.startDate),

    // Step E
    indoorOutdoorComfort: data.stepE.indoorOutdoorComfort,
    standingPatrolTolerance: data.stepE.standingPatrolTolerance,
    languagesSpoken: data.stepE.languagesSpoken.length > 0 ? data.stepE.languagesSpoken : undefined,
    isVeteran: data.stepE.isVeteran,
    eeoRace: emptyToUndefined(data.stepE.eeoRace),
    eeoGender: emptyToUndefined(data.stepE.eeoGender),
    eeoDisclosureConsent: data.stepE.eeoDisclosureConsent,

    // Step F
    consentPrivacy: data.stepF.consentPrivacy,
    consentMessaging: data.stepF.consentMessaging,
    consentVideoRecording: data.stepF.consentVideoRecording,
    signature: emptyToUndefined(data.stepF.signature),
    signatureDate: emptyToUndefined(data.stepF.signatureDate),

    // Metadata
    currentStep,
  };
}

/**
 * Convert API response to local ApplicationData format
 */
function fromApiFormat(apiData: ApplicationDataDto, existingData: ApplicationData): ApplicationData {
  return {
    ...existingData,
    id: existingData.id,
    applicationId: apiData.applicationNumber,
    status: apiData.status === 'submitted' ? 'submitted' : 'draft',
    updatedAt: apiData.updatedAt,
    submittedAt: apiData.submittedAt,

    stepA: {
      firstName: apiData.firstName ?? existingData.stepA.firstName,
      middleName: apiData.middleName ?? undefined,
      lastName: apiData.lastName ?? existingData.stepA.lastName,
      preferredName: apiData.preferredName ?? undefined,
      email: apiData.email ?? existingData.stepA.email,
      phone: apiData.phone ? fromE164(apiData.phone) : existingData.stepA.phone,
      address: apiData.address ?? existingData.stepA.address,
      city: apiData.city ?? existingData.stepA.city,
      state: apiData.state ?? existingData.stepA.state,
      zip: apiData.zip ?? existingData.stepA.zip,
      language: apiData.language ?? existingData.stepA.language,
    },

    stepB: {
      isOver18: apiData.isOver18 ?? existingData.stepB.isOver18,
      isAuthorizedToWork: apiData.isAuthorizedToWork ?? existingData.stepB.isAuthorizedToWork,
    },

    stepC: {
      licenses: existingData.stepC.licenses,
      licensingNotApplicable: apiData.licensingNotApplicable ?? existingData.stepC.licensingNotApplicable,
      florida: apiData.florida
        ? {
            hasClassD: apiData.florida.hasClassD ?? false,
            classDNumber: apiData.florida.classDNumber,
            classDExpiration: apiData.florida.classDExpiration,
            hasClassG: apiData.florida.hasClassG ?? false,
            classGNumber: apiData.florida.classGNumber,
            classGExpiration: apiData.florida.classGExpiration,
          }
        : existingData.stepC.florida,
      newYork: apiData.newYork
        ? {
            hasGuardCard: apiData.newYork.hasGuardCard ?? false,
            guardCardNumber: apiData.newYork.guardCardNumber,
            guardCardExpiration: apiData.newYork.guardCardExpiration,
            has8HourCert: apiData.newYork.has8HourCert ?? false,
            cert8HourDate: apiData.newYork.cert8HourDate,
            has16HourCert: apiData.newYork.has16HourCert ?? false,
            cert16HourDate: apiData.newYork.cert16HourDate,
            hasFirearmCreds: apiData.newYork.hasFirearmCreds ?? false,
            firearmCredsNumber: apiData.newYork.firearmCredsNumber,
            firearmCredsExpiration: apiData.newYork.firearmCredsExpiration,
          }
        : existingData.stepC.newYork,
      certifications: apiData.certifications
        ? {
            hasCprAed: apiData.certifications.hasCprAed ?? false,
            cprAedExpiration: apiData.certifications.cprAedExpiration,
            hasTwic: apiData.certifications.hasTwic ?? false,
            twicExpiration: apiData.certifications.twicExpiration,
          }
        : existingData.stepC.certifications,
    },

    stepD: {
      roles: apiData.roles ?? existingData.stepD.roles,
      locations: apiData.locations ?? existingData.stepD.locations,
      shifts: apiData.shifts ?? existingData.stepD.shifts,
      hasTransport: apiData.hasTransport ?? existingData.stepD.hasTransport,
      startDate: apiData.startDate ?? existingData.stepD.startDate,
    },

    stepE: {
      indoorOutdoorComfort: apiData.indoorOutdoorComfort ?? existingData.stepE.indoorOutdoorComfort,
      standingPatrolTolerance: apiData.standingPatrolTolerance ?? existingData.stepE.standingPatrolTolerance,
      languagesSpoken: apiData.languagesSpoken ?? existingData.stepE.languagesSpoken,
      isVeteran: apiData.isVeteran ?? existingData.stepE.isVeteran,
      eeoRace: existingData.stepE.eeoRace, // EEO data not returned from API
      eeoGender: existingData.stepE.eeoGender,
      eeoDisclosureConsent: existingData.stepE.eeoDisclosureConsent,
    },

    stepF: {
      consentPrivacy: apiData.consentPrivacy ?? existingData.stepF.consentPrivacy,
      consentMessaging: apiData.consentMessaging ?? existingData.stepF.consentMessaging,
      consentVideoRecording: apiData.consentVideoRecording ?? existingData.stepF.consentVideoRecording,
      signature: apiData.signature ?? existingData.stepF.signature,
      signatureDate: apiData.signatureDate ?? existingData.stepF.signatureDate,
    },
  };
}

/**
 * Merge prefill data into current application data.
 * Only fills fields that are currently empty/default — never overwrites existing user input.
 * stepF (consents) is never prefilled.
 */
function mergePrefillIntoData(current: ApplicationData, prefill: PrefillData): ApplicationData {
  const currentFlorida = current.stepC.florida;
  const currentNewYork = current.stepC.newYork;

  const mergedFlorida: typeof currentFlorida = currentFlorida
    ? {
        ...currentFlorida,
        classDNumber: currentFlorida.classDNumber ?? prefill.floridaSecurityLicenseNumber,
        classDExpiration: currentFlorida.classDExpiration ?? prefill.floridaSecurityLicenseExpiry,
      }
    : undefined;

  const mergedNewYork: typeof currentNewYork = currentNewYork
    ? {
        ...currentNewYork,
        guardCardNumber: currentNewYork.guardCardNumber ?? prefill.newYorkSecurityLicenseNumber,
        guardCardExpiration: currentNewYork.guardCardExpiration ?? prefill.newYorkSecurityLicenseExpiry,
      }
    : undefined;

  return {
    ...current,
    stepA: {
      ...current.stepA,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      firstName: current.stepA.firstName || prefill.firstName || current.stepA.firstName,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      lastName: current.stepA.lastName || prefill.lastName || current.stepA.lastName,
      middleName: current.stepA.middleName ?? prefill.middleName,
      preferredName: current.stepA.preferredName ?? prefill.preferredName,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      email: current.stepA.email || prefill.email || current.stepA.email,
      phone:
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        current.stepA.phone || (prefill.phone ? fromE164(prefill.phone) : '') || current.stepA.phone,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      address: current.stepA.address || prefill.address || current.stepA.address,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      city: current.stepA.city || prefill.city || current.stepA.city,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      state: current.stepA.state || prefill.state || current.stepA.state,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      zip: current.stepA.zip || prefill.zip || current.stepA.zip,
      language:
        current.stepA.language !== INITIAL_APPLICATION_DATA.stepA.language
          ? current.stepA.language
          : (prefill.language ?? current.stepA.language),
    },
    stepB: {
      ...current.stepB,
      isOver18:
        current.stepB.isOver18 !== INITIAL_APPLICATION_DATA.stepB.isOver18
          ? current.stepB.isOver18
          : (prefill.isOver18 ?? current.stepB.isOver18),
      isAuthorizedToWork:
        current.stepB.isAuthorizedToWork !== INITIAL_APPLICATION_DATA.stepB.isAuthorizedToWork
          ? current.stepB.isAuthorizedToWork
          : (prefill.isAuthorizedToWork ?? current.stepB.isAuthorizedToWork),
    },
    stepC: {
      ...current.stepC,
      florida: mergedFlorida,
      newYork: mergedNewYork,
    },
    stepD: {
      ...current.stepD,
      roles: current.stepD.roles.length > 0 ? current.stepD.roles : (prefill.roles ?? current.stepD.roles),
      locations:
        current.stepD.locations.length > 0 ? current.stepD.locations : (prefill.locations ?? current.stepD.locations),
      shifts: current.stepD.shifts.length > 0 ? current.stepD.shifts : (prefill.shifts ?? current.stepD.shifts),
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      startDate: current.stepD.startDate || prefill.startDate || current.stepD.startDate,
    },
    stepE: {
      ...current.stepE,
      indoorOutdoorComfort:
        current.stepE.indoorOutdoorComfort !== INITIAL_APPLICATION_DATA.stepE.indoorOutdoorComfort
          ? current.stepE.indoorOutdoorComfort
          : (prefill.preferIndoor ?? current.stepE.indoorOutdoorComfort),
      standingPatrolTolerance:
        current.stepE.standingPatrolTolerance !== INITIAL_APPLICATION_DATA.stepE.standingPatrolTolerance
          ? current.stepE.standingPatrolTolerance
          : (prefill.canPatrol ?? current.stepE.standingPatrolTolerance),
      languagesSpoken:
        current.stepE.languagesSpoken.length > 0
          ? current.stepE.languagesSpoken
          : (prefill.languages ?? current.stepE.languagesSpoken),
    },
  };
}

interface SaveResult {
  success: boolean;
  errors?: ValidationError[];
  warnings?: ValidationWarning[];
  conflict?: boolean;
}

interface ApplicationState {
  data: ApplicationData;
  currentStep: number;
  isSaving: boolean;
  isLoading: boolean;
  applicantId: string | null;
  serverApplicationId: string | null;
  jobPostingId: string | null;
  jobPostingTitle: string | null;
  jobPostingLocation: string | null;
  jobPostingSiteState: string | null;
  existingApplications: ExistingApplication[];
  prefillLoaded: boolean;

  // Actions
  setJobPosting: (jobPostingId: string, title?: string, location?: string, siteState?: string) => void;
  initForApplicant: (applicantId: string) => void;
  initApplication: () => void;
  loadDraft: () => Promise<boolean>;
  loadPrefill: () => Promise<boolean>;
  checkExistingApplications: () => Promise<ExistingApplication[]>;
  updateStepData: <K extends StepKeys>(step: K, data: Partial<ApplicationData[K]>) => void;
  saveAndValidateStep: (options?: { silent?: boolean }) => Promise<SaveResult>;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  saveProgress: () => Promise<SaveResult>;
  saveDraftQuietly: () => Promise<void>;
  submitApplication: () => Promise<SaveResult>;
  syncGuestDraft: () => Promise<void>;
  addWarning: (warning: string) => void;
  clearWarnings: () => void;
  resetApplication: () => void;
}

export const useApplicationStore = create<ApplicationState>()(
  persist(
    (set, get) => ({
      data: { ...INITIAL_APPLICATION_DATA, id: generateId() },
      currentStep: 0,
      isSaving: false,
      isLoading: false,
      applicantId: null,
      serverApplicationId: null,
      jobPostingId: null,
      jobPostingTitle: null,
      jobPostingLocation: null,
      jobPostingSiteState: null,
      existingApplications: [],
      prefillLoaded: false,

      setJobPosting: (jobPostingId, title, location, siteState) => {
        set({
          jobPostingId,
          jobPostingTitle: title ?? null,
          jobPostingLocation: location ?? null,
          jobPostingSiteState: siteState ?? null,
          serverApplicationId: null,
        });
      },

      initForApplicant: (applicantId) => {
        const currentApplicantId = get().applicantId;

        // If same applicant, keep existing data
        if (currentApplicantId === applicantId) {
          return;
        }

        // Different applicant - reset to fresh application
        set({
          applicantId,
          data: { ...INITIAL_APPLICATION_DATA, id: generateId() },
          currentStep: 0,
          serverApplicationId: null,
          jobPostingId: null,
          jobPostingTitle: null,
          jobPostingLocation: null,
          jobPostingSiteState: null,
        });
      },

      initApplication: () => {
        set({
          data: { ...INITIAL_APPLICATION_DATA, id: generateId() },
          currentStep: 0,
          serverApplicationId: null,
          jobPostingId: null,
          jobPostingTitle: null,
          jobPostingLocation: null,
          jobPostingSiteState: null,
        });
      },

      loadDraft: async () => {
        set({ isLoading: true });
        try {
          const { jobPostingId } = get();
          const response = await applicationApi.getDraft(jobPostingId ?? undefined);
          const apiData = response.data.application;

          set((state) => ({
            data: fromApiFormat(apiData, state.data),
            currentStep: apiData.currentStep,
            serverApplicationId: apiData.applicationId,
            isLoading: false,
          }));

          return true;
        } catch (error) {
          set({ isLoading: false });

          if (error instanceof ApiError && (error.statusCode === 404 || error.statusCode === 422)) {
            // No draft exists - start fresh (this is OK)
            return false;
          }

          if (error instanceof ApiError && error.statusCode === 422) {
            // Application limit or other validation error
            return false;
          }

          throw error;
        }
      },

      loadPrefill: async () => {
        try {
          const prefill = await applicationApi.getPrefill();
          if (!prefill) {
            return false;
          }

          set((state) => ({
            data: mergePrefillIntoData(state.data, prefill),
            prefillLoaded: true,
          }));

          return true;
        } catch {
          return false;
        }
      },

      checkExistingApplications: async () => {
        try {
          const response = await applicationApi.listApplications();
          const applications = response.data.applications;

          const existing: ExistingApplication[] = applications.map((app) => ({
            applicationId: app.applicationId,
            applicationNumber: app.applicationNumber,
            status: app.status,
            currentStep: app.currentStep,
            updatedAt: app.updatedAt,
            submittedAt: app.submittedAt,
            firstName: app.firstName,
            lastName: app.lastName,
            jobPostingId: app.jobPostingId,
            jobPostingTitle: app.jobPosting?.title,
            jobPostingRole: app.jobPosting?.role,
            jobPostingLocation: app.jobPosting ? `${app.jobPosting.siteCity}, ${app.jobPosting.siteState}` : undefined,
            jobPostingSiteState: app.jobPosting?.siteState,
            decisionContext: app.decisionContext,
          }));

          set({ existingApplications: existing });
          return existing;
        } catch {
          return [];
        }
      },

      updateStepData: <K extends StepKeys>(step: K, stepData: Partial<ApplicationData[K]>) => {
        set((state) => ({
          data: {
            ...state.data,
            [step]: {
              ...state.data[step],
              ...stepData,
            },
            updatedAt: new Date().toISOString(),
          },
        }));
      },

      saveAndValidateStep: async (options) => {
        const silent = options?.silent ?? false;
        const { data, currentStep, serverApplicationId, jobPostingId } = get();

        if (!silent) set({ isSaving: true });

        // Guest path: skip API call, rely on Zustand persist middleware for localStorage
        if (!isAuthenticated()) {
          if (!silent) set({ isSaving: false });
          return { success: true };
        }

        try {
          const requestData = toApiFormat(data, currentStep, jobPostingId, serverApplicationId);

          // Use PUT if we have an existing application, POST otherwise
          const response = serverApplicationId
            ? await applicationApi.updateDraft(requestData)
            : await applicationApi.saveDraft(requestData);

          const apiData = response.data.application;
          const errors = response.data.errors ?? [];
          const warnings = response.data.warnings ?? [];

          // Update local state with server response
          set((state) => ({
            data: fromApiFormat(apiData, state.data),
            serverApplicationId: apiData.applicationId,
            ...(silent ? {} : { isSaving: false }),
          }));

          return {
            success: errors.length === 0,
            errors: errors.length > 0 ? errors : undefined,
            warnings: warnings.length > 0 ? warnings : undefined,
          };
        } catch (error) {
          if (!silent) set({ isSaving: false });

          if (error instanceof ApiError && error.statusCode === 422 && error.errors) {
            return {
              success: false,
              errors: error.errors as ValidationError[],
            };
          }

          if (error instanceof ApiError && error.statusCode === 409) {
            return {
              success: false,
              conflict: true,
              errors: [{ field: 'application', message: error.message }] as ValidationError[],
            };
          }

          throw error;
        }
      },

      nextStep: () => {
        const { currentStep } = get();
        if (currentStep < 5) {
          set({ currentStep: currentStep + 1 });
        }
      },

      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 0) {
          set({ currentStep: currentStep - 1 });
        }
      },

      goToStep: (step) => {
        if (step >= 0 && step <= 5) {
          set({ currentStep: step });
        }
      },

      saveProgress: () => {
        return get().saveAndValidateStep();
      },

      saveDraftQuietly: async () => {
        if (autoSaveInFlight || get().isSaving) return;
        autoSaveInFlight = true;
        try {
          await get().saveAndValidateStep({ silent: true });
        } catch {
          // Silent failure — autosave errors should not disrupt the user
        } finally {
          autoSaveInFlight = false;
        }
      },

      submitApplication: async () => {
        const { data, currentStep, jobPostingId, serverApplicationId } = get();

        set({ isSaving: true });

        try {
          const requestData = toApiFormat(data, currentStep, jobPostingId, serverApplicationId);
          const response = await applicationApi.submitApplication(requestData);

          const apiData = response.data.application;
          const errors = response.data.errors ?? [];
          const warnings = response.data.warnings ?? [];

          // Only mark as submitted if there are no errors
          if (errors.length === 0) {
            // Store the submitted application info for SuccessPage
            const submittedApplicationId = apiData.applicationId;
            const submittedApplicationNumber = apiData.applicationNumber;

            // Reset to fresh state after successful submission
            set({
              data: {
                ...INITIAL_APPLICATION_DATA,
                id: generateId(),
                status: 'submitted',
                applicationId: submittedApplicationNumber,
                submittedAt: apiData.submittedAt ?? new Date().toISOString(),
              },
              serverApplicationId: submittedApplicationId,
              currentStep: 0,
              isSaving: false,
            });

            return {
              success: true,
              warnings: warnings.length > 0 ? warnings : undefined,
            };
          }

          // Submission had errors - don't mark as submitted
          set({ isSaving: false });

          return {
            success: false,
            errors,
            warnings: warnings.length > 0 ? warnings : undefined,
          };
        } catch (error) {
          set({ isSaving: false });

          if (error instanceof ApiError && error.statusCode === 422) {
            if (error.errors) {
              return {
                success: false,
                errors: error.errors as ValidationError[],
              };
            }
            // API returned 422 but without errors array - still a validation error
            return {
              success: false,
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              errors: [{ field: 'unknown', message: error.message || 'Validation failed' }],
            };
          }

          throw error;
        }
      },

      syncGuestDraft: async () => {
        const { data, serverApplicationId, currentStep, jobPostingId } = get();

        // Only sync if there's local data but no server-side application yet
        if (serverApplicationId) return;

        const hasLocalData = Boolean(data.stepA.firstName) || Boolean(data.stepA.email) || Boolean(data.stepA.phone);
        if (!hasLocalData) return;

        try {
          const requestData = toApiFormat(data, currentStep, jobPostingId, serverApplicationId);
          const response = await applicationApi.saveDraft(requestData);
          const apiData = response.data.application;

          set((state) => ({
            data: fromApiFormat(apiData, state.data),
            serverApplicationId: apiData.applicationId,
          }));
        } catch {
          // Guest draft sync is best-effort
        }
      },

      addWarning: (warning) => {
        set((state) => ({
          data: {
            ...state.data,
            warnings: [...(state.data.warnings ?? []), warning],
          },
        }));
      },

      clearWarnings: () => {
        set((state) => ({
          data: {
            ...state.data,
            warnings: [],
          },
        }));
      },

      resetApplication: () => {
        set({
          applicantId: null,
          data: { ...INITIAL_APPLICATION_DATA, id: generateId() },
          currentStep: 0,
          serverApplicationId: null,
        });
      },
    }),
    {
      name: 'kent-app-draft',
      version: 5, // Increment when data structure changes
      partialize: (state) => ({
        applicantId: state.applicantId,
        data: state.data,
        currentStep: state.currentStep,
        serverApplicationId: state.serverApplicationId,
        jobPostingId: state.jobPostingId,
        jobPostingTitle: state.jobPostingTitle,
        jobPostingLocation: state.jobPostingLocation,
      }),
      migrate: (persistedState, version) => {
        const state = persistedState as {
          data?: ApplicationData;
          applicantId?: string | null;
          currentStep?: number;
          serverApplicationId?: string | null;
          jobPostingId?: string | null;
          jobPostingTitle?: string | null;
          jobPostingLocation?: string | null;
        };
        if (version < 2 && state.data) {
          // Migrate old data to new structure
          return {
            ...state,
            data: migrateApplicationData(state.data),
          };
        }
        if (version < 3) {
          // Add serverApplicationId field
          return {
            ...state,
            serverApplicationId: null,
          };
        }
        if (version < 4) {
          // Add jobPostingId field
          return {
            ...state,
            jobPostingId: null,
          };
        }
        if (version < 5) {
          // Add jobPostingTitle and jobPostingLocation fields
          return {
            ...state,
            jobPostingTitle: null,
            jobPostingLocation: null,
            jobPostingSiteState: null,
          };
        }
        return state;
      },
    },
  ),
);
